import { Box, Typography, Paper, IconButton, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useState } from 'react';

interface InfoCardProps {
    label: string;
    value: string;
    copyable?: boolean;
    highlighted?: boolean;
    onCopySuccess?: (fieldName: string) => void;
    onCopyError?: (error: string) => void;
}

const InfoCard: React.FC<InfoCardProps> = ({
    label,
    value,
    copyable = true,
    highlighted = false,
    onCopySuccess,
    onCopyError
}) => {
    const [copiedField, setCopiedField] = useState<string | null>(null);

    const copyToClipboard = async (text: string, fieldName: string) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedField(fieldName);
            setTimeout(() => setCopiedField(null), 2000);
            onCopySuccess?.(fieldName);
        } catch (err) {
            onCopyError?.("Unable to copy to clipboard");
        }
    };

    return (
        <Paper
            elevation={0}
            sx={{
                p: 2,
                bgcolor: highlighted ? 'rgba(242, 103, 74, 0.15)' : 'rgba(255, 255, 255, 0.05)',
                border: highlighted ? '2px solid rgba(242, 103, 74, 0.5)' : '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 2,
                transition: 'all 0.2s ease',
                '&:hover': {
                    bgcolor: highlighted ? 'rgba(242, 103, 74, 0.2)' : 'rgba(255, 255, 255, 0.08)',
                    borderColor: highlighted ? 'rgba(242, 103, 74, 0.6)' : 'rgba(255, 255, 255, 0.2)',
                }
            }}
        >
            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box flex={1}>
                    <Typography
                        variant="caption"
                        sx={{
                            color: highlighted ? '#F2674A' : 'rgba(255, 255, 255, 0.6)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            fontSize: '0.7rem',
                            fontWeight: highlighted ? 700 : 600
                        }}
                    >
                        {label}
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: '#fff',
                            mt: 0.5,
                            fontWeight: highlighted ? 600 : 500,
                            wordBreak: 'break-word'
                        }}
                    >
                        {value}
                    </Typography>
                </Box>
                {copyable && (
                    <Tooltip title={copiedField === label ? "Copied!" : "Copy"}>
                        <IconButton
                            size="small"
                            onClick={() => copyToClipboard(value, label)}
                            sx={{
                                ml: 1,
                                color: copiedField === label ? '#4caf50' : highlighted ? '#F2674A' : 'rgba(255, 255, 255, 0.7)',
                                '&:hover': {
                                    bgcolor: highlighted ? 'rgba(242, 103, 74, 0.2)' : 'rgba(255, 255, 255, 0.1)',
                                }
                            }}
                        >
                            {copiedField === label ? (
                                <CheckCircleIcon fontSize="small" />
                            ) : (
                                <ContentCopyIcon fontSize="small" />
                            )}
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
        </Paper>
    );
};

export default InfoCard;